import React, { useState, useEffect, useRef } from 'react';
import {
  Receipt, Plus, Trash2, Upload, Eye, Calendar, Clock, AlertTriangle,
  CheckCircle, XCircle, DollarSign, MapPin, User, FileText, ChevronLeft,
  Camera, Info, Shield, Scale, Phone, ExternalLink, ChevronDown, ChevronUp,
  Bell, RefreshCw, Image as ImageIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, storage } from '../lib/supabase';

// ==================== CONSTANTS ====================
const TICKET_TIPS = [
  {
    title: "Pay Within 28 Days",
    content: "Traffic fines in Jamaica must be paid within 28 days. Paying within this period avoids additional penalties and court appearances.",
    icon: DollarSign,
    color: "emerald"
  },
  {
    title: "Keep Your Ticket Safe",
    content: "Tickets can fade over time. Upload a photo immediately so you always have a clear record of the ticket number, offence, and amount.",
    icon: Camera,
    color: "blue"
  },
  {
    title: "Know Your Rights",
    content: "You have the right to contest a ticket in court. However, if you lose, you may face additional court costs. Weigh your options carefully.",
    icon: Scale,
    color: "purple"
  },
  {
    title: "Payment Locations",
    content: "Traffic fines can be paid at any Tax Administration Jamaica (TAJ) office, or online through the TAJ portal at jamaicatax.gov.jm",
    icon: MapPin,
    color: "amber"
  },
  {
    title: "Request a Receipt",
    content: "Always get a receipt when paying your fine. This is your proof of payment if any disputes arise later.",
    icon: FileText,
    color: "rose"
  }
];

const OFFENCE_OPTIONS = [
  { value: 'speeding', label: 'Speeding' },
  { value: 'no_licence', label: 'No Driver\'s Licence' },
  { value: 'expired_licence', label: 'Expired Driver\'s Licence' },
  { value: 'no_insurance', label: 'No Insurance' },
  { value: 'expired_insurance', label: 'Expired Insurance' },
  { value: 'no_fitness', label: 'No Fitness Certificate' },
  { value: 'expired_fitness', label: 'Expired Fitness' },
  { value: 'no_registration', label: 'No Registration' },
  { value: 'no_seatbelt', label: 'No Seatbelt' },
  { value: 'phone_driving', label: 'Using Phone While Driving' },
  { value: 'red_light', label: 'Running Red Light' },
  { value: 'illegal_parking', label: 'Illegal Parking' },
  { value: 'tinted_windows', label: 'Illegal Tint' },
  { value: 'careless_driving', label: 'Careless Driving' },
  { value: 'dangerous_driving', label: 'Dangerous Driving' },
  { value: 'dui', label: 'DUI / Drunk Driving' },
  { value: 'other', label: 'Other Offence' }
];

// ==================== HELPER FUNCTIONS ====================
const getDaysRemaining = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getStatusInfo = (status, daysRemaining) => {
  if (status === 'paid') {
    return { color: 'emerald', bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'PAID', icon: CheckCircle };
  }
  if (status === 'contested') {
    return { color: 'blue', bg: 'bg-blue-100', text: 'text-blue-700', label: 'CONTESTED', icon: Scale };
  }
  if (status === 'overdue' || (daysRemaining !== null && daysRemaining < 0)) {
    return { color: 'red', bg: 'bg-red-100', text: 'text-red-700', label: 'OVERDUE', icon: AlertTriangle };
  }
  if (daysRemaining !== null && daysRemaining <= 3) {
    return { color: 'amber', bg: 'bg-amber-100', text: 'text-amber-700', label: 'DUE SOON', icon: Clock };
  }
  return { color: 'gray', bg: 'bg-gray-100', text: 'text-gray-700', label: 'UNPAID', icon: Receipt };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-JM', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// ==================== TIPS SECTION ====================
const TipsSection = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Info className="w-5 h-5 text-amber-600" />
          <span className="font-bold text-lg text-gray-800">Tips & Important Information</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && (
        <div className="p-6 space-y-4">
          {TICKET_TIPS.map((tip, index) => {
            const Icon = tip.icon;
            return (
              <div key={index} className={`flex items-start gap-4 p-4 bg-${tip.color}-50 rounded-xl`}>
                <div className={`w-10 h-10 bg-${tip.color}-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 text-${tip.color}-600`} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">{tip.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{tip.content}</p>
                </div>
              </div>
            );
          })}

          {/* Quick Links */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h4 className="font-bold text-gray-800 mb-3">Useful Links</h4>
            <div className="space-y-2">
              <a
                href="https://jamaicatax.gov.jm"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                TAJ Online Payment Portal
              </a>
              <a
                href="tel:1-888-829-4672"
                className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm"
              >
                <Phone className="w-4 h-4" />
                TAJ Helpline: 1-888-TAJ-4TAX
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ADD TICKET FORM ====================
const AddTicketForm = ({ onSave, onCancel, isUploading }) => {
  const [form, setForm] = useState({
    ticket_number: '',
    offence_type: '',
    date_received: new Date().toISOString().split('T')[0],
    due_date: '',
    amount: '',
    location: '',
    officer_badge: '',
    notes: '',
    status: 'unpaid'
  });
  const [ticketImage, setTicketImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Auto-calculate due date (28 days from received date - Jamaica standard)
  useEffect(() => {
    if (form.date_received && !form.due_date) {
      const received = new Date(form.date_received);
      received.setDate(received.getDate() + 28);
      setForm(prev => ({ ...prev, due_date: received.toISOString().split('T')[0] }));
    }
  }, [form.date_received]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be less than 10MB');
      return;
    }

    setTicketImage(file);
    const reader = new FileReader();
    reader.onload = (event) => setImagePreview(event.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.ticket_number) {
      alert('Please enter a ticket number');
      return;
    }
    onSave(form, ticketImage);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
        <Plus className="w-5 h-5 text-purple-600" />
        Add New Ticket
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Ticket Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Camera className="w-4 h-4 inline mr-1" />
            Ticket Photo (Recommended)
          </label>
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-purple-500 transition-colors"
          >
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="Ticket" className="max-h-48 max-w-full mx-auto rounded-lg object-contain" />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTicketImage(null);
                    setImagePreview(null);
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Click to upload ticket photo</p>
                <p className="text-xs text-gray-400 mt-1">Tickets fade - save a clear copy</p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Number *</label>
            <input
              type="text"
              value={form.ticket_number}
              onChange={e => setForm({ ...form, ticket_number: e.target.value })}
              placeholder="e.g., TK-12345678"
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Offence Type</label>
            <select
              value={form.offence_type}
              onChange={e => setForm({ ...form, offence_type: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            >
              <option value="">Select offence...</option>
              {OFFENCE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Date Received
            </label>
            <input
              type="date"
              value={form.date_received}
              onChange={e => setForm({ ...form, date_received: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Clock className="w-4 h-4 inline mr-1" />
              Due Date (Last Day to Pay)
            </label>
            <input
              type="date"
              value={form.due_date}
              onChange={e => setForm({ ...form, due_date: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Fine Amount (JMD)
            </label>
            <input
              type="number"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              placeholder="e.g., 5000"
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Officer Badge #
            </label>
            <input
              type="text"
              value={form.officer_badge}
              onChange={e => setForm({ ...form, officer_badge: e.target.value })}
              placeholder="Badge number"
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              Location
            </label>
            <input
              type="text"
              value={form.location}
              onChange={e => setForm({ ...form, location: e.target.value })}
              placeholder="Where were you ticketed?"
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Any additional notes..."
              rows={2}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            />
          </div>
        </div>

        {/* Reminder Notice */}
        <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-800">Automatic Reminders</p>
              <p className="text-sm text-purple-600">
                We'll send you email reminders 10 days, 5 days, and 1 day before your due date.
              </p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isUploading}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Save Ticket
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-4 rounded-xl"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ==================== TICKET CARD ====================
const TicketCard = ({ ticket, onUpdateStatus, onDelete, onViewImage }) => {
  const daysRemaining = getDaysRemaining(ticket.due_date);
  const statusInfo = getStatusInfo(ticket.status, daysRemaining);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header with status */}
      <div className={`${statusInfo.bg} px-5 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-5 h-5 ${statusInfo.text}`} />
          <span className={`font-bold text-sm ${statusInfo.text}`}>{statusInfo.label}</span>
        </div>
        {daysRemaining !== null && ticket.status !== 'paid' && (
          <span className={`text-sm font-medium ${daysRemaining < 0 ? 'text-red-700' : daysRemaining <= 3 ? 'text-amber-700' : 'text-gray-600'}`}>
            {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` :
             daysRemaining === 0 ? 'Due today!' :
             `${daysRemaining} days remaining`}
          </span>
        )}
      </div>

      {/* Main content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h4 className="font-bold text-xl">#{ticket.ticket_number}</h4>
              {(ticket.image_display_url || ticket.image_url) && (
                <button
                  onClick={() => onViewImage(ticket.image_display_url || ticket.image_url)}
                  className="p-1 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  title="View ticket photo"
                >
                  <ImageIcon className="w-4 h-4 text-gray-600" />
                </button>
              )}
            </div>
            <p className="text-gray-600">
              {OFFENCE_OPTIONS.find(o => o.value === ticket.offence_type)?.label || ticket.offence_type || 'Unknown offence'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-black text-gray-800">
              ${(parseFloat(ticket.amount) || 0).toLocaleString()}
            </p>
            <p className="text-sm text-gray-500">JMD</p>
          </div>
        </div>

        {/* Date info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 p-3 bg-gray-50 rounded-xl">
          <div>
            <p className="text-xs text-gray-500 uppercase">Date Received</p>
            <p className="font-medium">{formatDate(ticket.date_received)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Due Date</p>
            <p className={`font-medium ${daysRemaining !== null && daysRemaining < 0 ? 'text-red-600' : ''}`}>
              {formatDate(ticket.due_date)}
            </p>
          </div>
        </div>

        {/* Additional info */}
        {(ticket.location || ticket.officer_badge) && (
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
            {ticket.location && (
              <span className="flex items-center gap-1 break-words max-w-full">
                <MapPin className="w-4 h-4 flex-shrink-0" /> <span className="break-words">{ticket.location}</span>
              </span>
            )}
            {ticket.officer_badge && (
              <span className="flex items-center gap-1">
                <User className="w-4 h-4 flex-shrink-0" /> Badge: {ticket.officer_badge}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 pt-4 border-t space-y-3">
          {/* Mark as Paid button - prominent for unpaid tickets */}
          {ticket.status !== 'paid' && (
            <button
              onClick={() => onUpdateStatus(ticket.id, 'paid')}
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-colors"
            >
              <CheckCircle className="w-5 h-5" />
              Mark as Paid
            </button>
          )}

          {/* Secondary actions */}
          <div className="flex gap-2">
            {ticket.status === 'paid' ? (
              <button
                onClick={() => onUpdateStatus(ticket.id, 'unpaid')}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium"
              >
                Mark as Unpaid
              </button>
            ) : (
              <select
                value={ticket.status}
                onChange={e => onUpdateStatus(ticket.id, e.target.value)}
                className="flex-1 px-4 py-2 rounded-xl border-2 border-gray-200 text-sm font-medium focus:border-purple-500"
              >
                <option value="unpaid">Unpaid</option>
                <option value="contested">Contested (In Court)</option>
                <option value="overdue">Mark Overdue</option>
              </select>
            )}
            <button
              onClick={() => onDelete(ticket.id)}
              className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl"
              title="Delete ticket"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== IMAGE VIEWER MODAL ====================
const ImageViewerModal = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="max-w-4xl max-h-[90vh] relative">
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white"
        >
          <XCircle className="w-6 h-6" />
        </button>
        <img src={imageUrl} alt="Ticket" className="max-h-[80vh] rounded-lg" />
      </div>
    </div>
  );
};

// ==================== MAIN TICKET TRACKER PAGE ====================
export const TicketTrackerPage = ({ setPage, goBack }) => {
  const { user, isAuthenticated } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);

  // Load tickets
  useEffect(() => {
    loadTickets();
  }, [user]);

  const loadTickets = async () => {
    setLoading(true);

    if (isAuthenticated && user) {
      // Load from database
      const { data, error } = await supabase
        ?.from('user_tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (!error && data) {
        // The ticket-images bucket is private: image_url stores the storage
        // path, so resolve a short-lived signed URL for display. Full http
        // URLs (legacy rows) pass through unchanged.
        const withImages = await Promise.all(data.map(async (t) => {
          if (t.image_url && !/^https?:\/\//i.test(t.image_url)) {
            const { data: signed } = await storage.getSignedUrl('ticket-images', t.image_url);
            return { ...t, image_display_url: signed?.signedUrl || null };
          }
          return { ...t, image_display_url: t.image_url };
        }));
        setTickets(withImages);
      } else if (error) {
        console.error('Error loading tickets:', error);
      }
    } else {
      // Load from localStorage
      const stored = localStorage.getItem('ja-tickets');
      if (stored) {
        try {
          setTickets(JSON.parse(stored));
        } catch (e) {
          setTickets([]);
        }
      }
    }

    setLoading(false);
  };

  const handleSaveTicket = async (form, imageFile) => {
    setIsUploading(true);

    try {
      let imageUrl = null;

      // Upload image if provided. The bucket is private, so store the storage
      // PATH — display resolves it to a signed URL.
      if (imageFile && isAuthenticated && user) {
        const ext = imageFile.name.split('.').pop();
        const path = `${user.id}/${Date.now()}_ticket.${ext}`;
        const { error: uploadError } = await storage.uploadImage('ticket-images', path, imageFile);

        if (uploadError) {
          if (!window.confirm(`Image upload failed (${uploadError.message}). Save the ticket without the photo?`)) {
            setIsUploading(false);
            return;
          }
        } else {
          imageUrl = path;
        }
      }

      const ticketData = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        image_url: imageUrl,
        reminder_10_sent: false,
        reminder_5_sent: false,
        reminder_1_sent: false
      };

      if (isAuthenticated && user) {
        // Save to database
        const { data, error } = await supabase
          ?.from('user_tickets')
          .insert({
            user_id: user.id,
            ...ticketData
          })
          .select()
          .single();

        if (error) throw error;
        if (data) {
          let displayUrl = data.image_url;
          if (displayUrl && !/^https?:\/\//i.test(displayUrl)) {
            const { data: signed } = await storage.getSignedUrl('ticket-images', displayUrl);
            displayUrl = signed?.signedUrl || null;
          }
          setTickets(prev => [{ ...data, image_display_url: displayUrl }, ...prev]);
        }
      } else {
        // Save to localStorage
        const newTicket = {
          id: Date.now(),
          ...ticketData,
          created_at: new Date().toISOString()
        };
        const updated = [newTicket, ...tickets];
        setTickets(updated);
        localStorage.setItem('ja-tickets', JSON.stringify(updated));
      }

      setShowForm(false);
    } catch (error) {
      console.error('Error saving ticket:', error);
      alert('Error saving ticket. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    if (isAuthenticated && user) {
      const { error } = await supabase
        ?.from('user_tickets')
        .update({ status: newStatus })
        .eq('id', ticketId);
      if (error) {
        alert('Error updating status: ' + error.message);
        return;
      }
    }

    const updated = tickets.map(t =>
      t.id === ticketId ? { ...t, status: newStatus } : t
    );
    setTickets(updated);

    if (!isAuthenticated) {
      localStorage.setItem('ja-tickets', JSON.stringify(updated));
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Delete this ticket? This cannot be undone.')) return;

    if (isAuthenticated && user) {
      const { error } = await supabase
        ?.from('user_tickets')
        .delete()
        .eq('id', ticketId);
      if (error) {
        alert('Error deleting ticket: ' + error.message);
        return;
      }
    }

    const updated = tickets.filter(t => t.id !== ticketId);
    setTickets(updated);

    if (!isAuthenticated) {
      localStorage.setItem('ja-tickets', JSON.stringify(updated));
    }
  };

  // Calculate stats
  const unpaidTickets = tickets.filter(t => t.status === 'unpaid' || t.status === 'overdue');
  const totalUnpaid = unpaidTickets.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
  const overdueCount = tickets.filter(t => {
    const days = getDaysRemaining(t.due_date);
    return t.status !== 'paid' && days !== null && days < 0;
  }).length;
  const dueSoonCount = tickets.filter(t => {
    const days = getDaysRemaining(t.due_date);
    return t.status !== 'paid' && days !== null && days >= 0 && days <= 5;
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => goBack ? goBack() : setPage('home')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
      >
        <ChevronLeft className="w-5 h-5" />Back
      </button>

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Receipt className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-black">Ticket Tracker</h2>
              <p className="text-purple-200 text-sm">Keep track of your traffic tickets</p>
            </div>
          </div>
          {totalUnpaid > 0 && (
            <div className="text-right">
              <p className="text-sm text-purple-200">Outstanding</p>
              <p className="text-3xl font-black">${totalUnpaid.toLocaleString()}</p>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        {tickets.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6 pt-6 border-t border-purple-500/30">
            <div className="text-center">
              <p className="text-3xl font-bold">{tickets.length}</p>
              <p className="text-purple-200 text-sm">Total</p>
            </div>
            <div className="text-center">
              <p className={`text-3xl font-bold ${overdueCount > 0 ? 'text-red-300' : ''}`}>
                {overdueCount}
              </p>
              <p className="text-purple-200 text-sm">Overdue</p>
            </div>
            <div className="text-center">
              <p className={`text-3xl font-bold ${dueSoonCount > 0 ? 'text-amber-300' : ''}`}>
                {dueSoonCount}
              </p>
              <p className="text-purple-200 text-sm">Due Soon</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Ticket Button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-4 rounded-xl shadow-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add New Ticket
        </button>
      )}

      {/* Add Ticket Form */}
      {showForm && (
        <AddTicketForm
          onSave={handleSaveTicket}
          onCancel={() => setShowForm(false)}
          isUploading={isUploading}
        />
      )}

      {/* Tips Section */}
      <TipsSection />

      {/* Tickets List */}
      <div className="space-y-4">
        {tickets.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-xl font-bold text-gray-600">No Tickets Recorded</h4>
            <p className="text-gray-500 mt-2">Add your first ticket to start tracking</p>
          </div>
        ) : (
          tickets.map(ticket => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              onUpdateStatus={handleUpdateStatus}
              onDelete={handleDeleteTicket}
              onViewImage={setViewingImage}
            />
          ))
        )}
      </div>

      {/* Login prompt for non-authenticated users */}
      {!isAuthenticated && tickets.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-800">Want reminders?</p>
              <p className="text-sm text-purple-600">
                Sign in to receive email reminders before your tickets are due.
              </p>
              <button
                onClick={() => setPage('login')}
                className="mt-2 text-sm font-medium text-purple-700 hover:text-purple-800 underline"
              >
                Sign in now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      <ImageViewerModal
        imageUrl={viewingImage}
        onClose={() => setViewingImage(null)}
      />
    </div>
  );
};

export default TicketTrackerPage;
